import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

interface ProcessSectionProps {
  eyebrow?: string;
  headline?: string;
  subheadline?: string;
  steps?: ProcessStep[];
  serviceName?: string; // For dynamic HowTo schema
  variant?: "default" | "compact"; // default for homepage, compact for service pages
}

const ProcessSection = ({
  eyebrow: eyebrowProp,
  headline: headlineProp,
  subheadline: subheadlineProp,
  steps: stepsProp,
  serviceName,
  variant = "default",
}: ProcessSectionProps = {}) => {
  const { t } = useTranslation('home');

  // Use translations as defaults, props override
  const eyebrow = eyebrowProp || t('process.eyebrow');
  const headline = headlineProp || t('process.headline');
  const subheadline = subheadlineProp || t('process.subheadline');

  // Default steps using translations
  const defaultSteps: ProcessStep[] = [
    {
      number: "01",
      title: t('process.step1.title'),
      description: t('process.step1.description'),
    },
    {
      number: "02",
      title: t('process.step2.title'),
      description: t('process.step2.description'),
    },
    {
      number: "03",
      title: t('process.step3.title'),
      description: t('process.step3.description'),
    },
    {
      number: "04",
      title: t('process.step4.title'),
      description: t('process.step4.description'),
    },
    {
      number: "05",
      title: t('process.step5.title'),
      description: t('process.step5.description'),
    },
  ];

  const steps = stepsProp || defaultSteps;
  // Generate HowTo Schema for SEO and LLM discoverability
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": serviceName
      ? `How to Get ${serviceName} in Houston`
      : "How to Get Your Roof Repaired or Replaced in Houston",
    "description": serviceName
      ? `${subheadline} Professional ${serviceName.toLowerCase()} services from Altum Contractors in Houston, TX.`
      : "A simple 5-step process for getting professional roofing services from Altum Contractors in Houston, TX. From free inspection to final walkthrough.",
    "totalTime": "P7D",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": "0",
      "description": "Free inspection and estimate - no obligation"
    },
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.title,
      "text": step.description,
      ...(index === 0 ? { "url": "https://altumcontractors.com/book-inspection" } : {})
    }))
  };

  const [headerVisible, setHeaderVisible] = useState(false);
  const [itemsVisible, setItemsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setHeaderVisible(true);
      },
      { threshold: 0.5 }
    );
    const itemsObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setItemsVisible(true);
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) headerObserver.observe(headerRef.current);
    if (itemsRef.current) itemsObserver.observe(itemsRef.current);

    return () => {
      headerObserver.disconnect();
      itemsObserver.disconnect();
    };
  }, []);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>
      <section
        ref={sectionRef}
        id="process"
        className="py-20 md:py-28 bg-textured border-t border-border overflow-hidden"
      >
        <div className="container mx-auto px-4 max-w-4xl">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`mb-16 text-center md:text-left transition-all duration-1000 ease-out ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="section-eyebrow">{eyebrow}</span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-navy mb-4">
            {headline}
          </h2>
          <p
            className={`text-lg text-charcoal/80 max-w-[700px] transition-all duration-1000 ease-out delay-200 ${
              headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {subheadline}
          </p>
        </div>

        {/* Steps with Watermark Numbers */}
        <div ref={itemsRef} className="space-y-12 md:space-y-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`flex items-start gap-6 md:gap-10 transition-all duration-700 ease-out ${
                itemsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${index * 120}ms` }}
            >
              {/* Large Faded Number with scale + slide animation */}
              <span 
                className={`text-7xl md:text-8xl lg:text-9xl font-heading font-bold text-primary/10 leading-none select-none shrink-0 -mt-2 md:-mt-4 transition-all duration-700 ease-out ${
                  itemsVisible ? "opacity-100 scale-100 translate-x-0" : "opacity-0 scale-90 -translate-x-4"
                }`}
                style={{ transitionDelay: `${index * 120}ms` }}
              >
                {step.number}
              </span>
              
              {/* Content with staggered text reveal */}
              <div className="pt-2 md:pt-4">
                <h3 
                  className={`text-xl md:text-2xl font-heading font-bold text-navy mb-2 transition-all duration-500 ease-out ${
                    itemsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
                  }`}
                  style={{ transitionDelay: `${index * 120 + 80}ms` }}
                >
                  {step.title}
                </h3>
                <p 
                  className={`text-muted-foreground leading-relaxed transition-all duration-500 ease-out ${
                    itemsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
                  }`}
                  style={{ transitionDelay: `${index * 120 + 160}ms` }}
                >
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
    </>
  );
};

export default ProcessSection;
